/**
 * Veilans Quick View — Lovable-style split modal with dynamic product data.
 * Triggers: [data-veilans-qv-open][data-veilans-qv-target]
 * Data: <script type="application/json" id="...">
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * @typedef {object} VeilansQvSwatch
 * @property {string} value
 * @property {string | null} color
 * @property {string | null} image
 */

/**
 * @typedef {object} VeilansQvVariant
 * @property {number|string} id
 * @property {boolean} available
 * @property {string} price
 * @property {string | null} image
 * @property {string | null} option1
 * @property {string | null} option2
 * @property {string | null} option3
 */

/**
 * @typedef {object} VeilansQvProduct
 * @property {number|string} id
 * @property {string} title
 * @property {string} url
 * @property {string} blurb
 * @property {string} price
 * @property {string} image
 * @property {string | null} colourOption
 * @property {VeilansQvSwatch[]} swatches
 * @property {VeilansQvVariant[]} variants
 */

class VeilansQuickView extends HTMLElement {
  /** @type {VeilansQvProduct | null} */
  #product = null;
  /** @type {string | null} */
  #selectedColour = null;
  /** @type {VeilansQvVariant | null} */
  #selectedVariant = null;
  /** @type {(e: MouseEvent) => void} */
  #onDialogClick = (e) => {
    const dialog = this.#dialog;
    if (!dialog?.open) return;
    if (e.target === dialog) this.close();
  };

  connectedCallback() {
    this.#closeButton?.addEventListener('click', () => this.close());
    this.#addButton?.addEventListener('click', () => this.#addToBag());
    this.#dialog?.addEventListener('click', this.#onDialogClick);
    this.#dialog?.addEventListener('cancel', (e) => {
      e.preventDefault();
      this.close();
    });

    document.addEventListener('click', this.#onTriggerClick);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onTriggerClick);
    this.#dialog?.removeEventListener('click', this.#onDialogClick);
  }

  /** @param {MouseEvent} event */
  #onTriggerClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-veilans-qv-open]');
    if (!(trigger instanceof HTMLElement)) return;

    event.preventDefault();
    event.stopPropagation();

    const dataId = trigger.getAttribute('data-veilans-qv-target');
    if (!dataId) return;

    const script = document.getElementById(dataId);
    if (!(script instanceof HTMLScriptElement)) return;

    try {
      /** @type {VeilansQvProduct} */
      const product = JSON.parse(script.textContent || '{}');
      this.open(product);
    } catch (error) {
      console.error('[veilans-quick-view] Invalid product JSON', error);
    }
  };

  get #dialog() {
    return this.querySelector('dialog');
  }

  get #image() {
    return this.querySelector('[ref="image"]');
  }

  get #title() {
    return this.querySelector('[ref="title"]');
  }

  get #blurb() {
    return this.querySelector('[ref="blurb"]');
  }

  get #price() {
    return this.querySelector('[ref="price"]');
  }

  get #colours() {
    return this.querySelector('[ref="colours"]');
  }

  get #colourName() {
    return this.querySelector('[ref="colourName"]');
  }

  get #swatches() {
    return this.querySelector('[ref="swatches"]');
  }

  get #addButton() {
    return this.querySelector('[ref="addButton"]');
  }

  get #detailsLink() {
    return this.querySelector('[ref="detailsLink"]');
  }

  get #status() {
    return this.querySelector('[ref="status"]');
  }

  get #closeButton() {
    return this.querySelector('[ref="closeButton"]');
  }

  /**
   * @param {VeilansQvProduct} product
   */
  open(product) {
    this.#product = product;
    this.#selectedColour = product.swatches?.[0]?.value ?? null;
    this.#selectedVariant = this.#resolveVariant();
    this.#render();
    this.#dialog?.showModal();
    document.documentElement.classList.add('veilans-qv-open');
  }

  close() {
    const dialog = this.#dialog;
    if (!dialog?.open) return;
    dialog.close();
    document.documentElement.classList.remove('veilans-qv-open');
    this.#setStatus('');
  }

  #render() {
    const product = this.#product;
    if (!product) return;

    const variant = this.#selectedVariant;
    const imageSrc = variant?.image || product.image || '';

    if (this.#image instanceof HTMLImageElement) {
      this.#image.src = imageSrc;
      this.#image.alt = product.title;
    }

    if (this.#title) this.#title.textContent = product.title;
    if (this.#blurb) {
      this.#blurb.textContent = product.blurb || '';
      this.#blurb.hidden = !product.blurb;
    }
    if (this.#price) this.#price.textContent = variant?.price || product.price || '';

    if (this.#detailsLink instanceof HTMLAnchorElement) {
      this.#detailsLink.href = product.url;
    }

    this.#renderSwatches();
    this.#syncAddButton();
  }

  #renderSwatches() {
    const product = this.#product;
    const colours = this.#colours;
    const swatchesEl = this.#swatches;
    if (!product || !colours || !swatchesEl) return;

    const swatches = product.swatches || [];
    if (!product.colourOption || swatches.length === 0) {
      colours.hidden = true;
      swatchesEl.innerHTML = '';
      return;
    }

    colours.hidden = false;
    if (this.#colourName) this.#colourName.textContent = this.#selectedColour || '';

    swatchesEl.innerHTML = '';
    for (const swatch of swatches) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'veilans-qv__swatch';
      button.setAttribute('role', 'option');
      button.setAttribute('aria-label', swatch.value);
      button.setAttribute('aria-selected', String(swatch.value === this.#selectedColour));
      button.title = swatch.value;
      if (swatch.available === false) {
        button.classList.add('veilans-qv__swatch--unavailable');
        button.title = `${swatch.value} (sold out)`;
      }

      const dot = document.createElement('span');
      dot.className = 'veilans-qv__swatch-dot';
      if (swatch.color) {
        dot.style.backgroundColor = swatch.color;
      } else if (swatch.image) {
        dot.style.backgroundImage = `url(${swatch.image})`;
      } else {
        dot.style.backgroundColor = '#6b7280';
      }
      button.appendChild(dot);

      button.addEventListener('click', () => {
        this.#selectedColour = swatch.value;
        this.#selectedVariant = this.#resolveVariant();
        this.#render();
      });

      swatchesEl.appendChild(button);
    }
  }

  /** @returns {VeilansQvVariant | null} */
  #resolveVariant() {
    const product = this.#product;
    if (!product?.variants?.length) return null;

    if (!this.#selectedColour || !product.colourOption) {
      return product.variants.find((v) => v.available) || product.variants[0] || null;
    }

    const optionKey =
      product.colourOption === 'option1'
        ? 'option1'
        : product.colourOption === 'option2'
          ? 'option2'
          : 'option3';

    const match = product.variants.find((v) => v[optionKey] === this.#selectedColour);
    return match || product.variants[0] || null;
  }

  #syncAddButton() {
    const button = this.#addButton;
    if (!(button instanceof HTMLButtonElement)) return;

    const available = Boolean(this.#selectedVariant?.available);
    button.disabled = !available;
    button.textContent = available ? 'Add to bag' : 'Sold out';
  }

  /** @param {string} message */
  #setStatus(message) {
    const status = this.#status;
    if (!(status instanceof HTMLElement)) return;
    if (!message) {
      status.hidden = true;
      status.textContent = '';
      return;
    }
    status.hidden = false;
    status.textContent = message;
  }

  async #addToBag() {
    const variant = this.#selectedVariant;
    const button = this.#addButton;
    if (!variant?.available || !(button instanceof HTMLButtonElement)) return;

    const variantId = String(variant.id);
    button.disabled = true;
    const originalLabel = button.textContent;
    button.textContent = 'Adding…';
    this.#setStatus('');

    const deferred = CartLinesUpdateEvent.createPromise();
    const dialog = this.#dialog;
    const eventTarget = dialog instanceof HTMLElement ? dialog : this;

    eventTarget.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const cartAddUrl = this.dataset.cartAddUrl || '/cart/add.js';
      const cartUrl = this.dataset.cartUrl || '/cart';

      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add to bag');
      }

      const cartResponse = await fetch(`${cartUrl}.json`);
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'veilans-quick-view',
          itemCount: 1,
          productId: String(this.#product?.id ?? ''),
          didError: false,
        },
      });

      button.textContent = 'Added';
      this.#setStatus('Added to bag');

      setTimeout(() => {
        button.textContent = originalLabel || 'Add to bag';
        button.disabled = !this.#selectedVariant?.available;
        this.close();
      }, 550);
    } catch (error) {
      deferred.reject(error);
      console.error('[veilans-quick-view] Add to bag failed', error);
      button.textContent = originalLabel || 'Add to bag';
      button.disabled = !this.#selectedVariant?.available;
      this.#setStatus(error instanceof Error ? error.message : 'Could not add to bag');
    }
  }
}

if (!customElements.get('veilans-quick-view')) {
  customElements.define('veilans-quick-view', VeilansQuickView);
}
